import React, { useState, useEffect, useReducer, useRef } from "react";

import {
  getItems,
  uploadFile,
  createCloudBuildOp,
  getItemAndListen,
} from "../apis/firebase";
import { saveContent } from "../apis/firebase";
import {
  CLOUD_BUILD_OPERATIONS,
  ContentType,
  JobStatus,
  PER_PAGE,
} from "../constants";

function useCollection(collectionName: string, orderBy: string) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoadingFlag] = useState(true);
  const [showPagination, setShowPaginationFlag] = useState(false);
  async function fetch() {
    const docs = await getItems(collectionName, orderBy, page);
    let fetchedItems = docs.map((document) => ({
      id: document.id,
      ...document.data(),
    }));
    setPageLoadingFlag(false);
    setItems(fetchedItems);
    setShowPaginationFlag(fetchedItems.length === PER_PAGE * page);
  }
  function viewMore() {
    setPageLoadingFlag(true);
    setPage(page + 1);
  }
  useEffect(() => {
    fetch();
  }, [page]);
  return [
    items,
    fetch,
    {
      viewMore,
      loading: pageLoading,
      show: showPagination,
    },
  ];
}

export function useCampaigns(): CampaignsManager {
  return useCollection("posts", "createdAt") as CampaignsManager;
}
export function useJobs(): JobsManager {
  return useCollection("jobs", "date") as JobsManager;
}
export function useCampaign(id: string): [Post | null] {
  const [item, setItem] = useState(null);
  useEffect(() => {
    let u = () => {};
    getItemAndListen("posts", id, (data, unsubscribe) => {
      u = unsubscribe;
      setItem(data);
    });
    return () => u();
  }, []);
  return [item];
}
export function useJob(id: string, defaultData: Job | null = null) {
  const [item, setItem] = useState(defaultData);
  const unsub = useRef(() => {});
  useEffect(() => {
    let u = () => {};
    getItemAndListen("jobs", id, (data, unsubscribe) => {
      u = unsubscribe;
      unsub.current = unsubscribe;
      setItem(data);
      if (
        data.status === JobStatus.SUCCESS ||
        data.status === JobStatus.FAILED
      ) {
        unsubscribe();
      }
    });
    return () => u();
  }, []);
  return [item, unsub.current as Function];
}

/* ****************************** files ********************************** */
type ContentFile = {
  name: string;
  uploaded: number;
  task: { cancel: Function };
  done: boolean;
};
function filesReducer(state, action) {
  if (action.type === "initial") {
    return action.value;
  } else if (action.type === "success") {
    state[action.index].done = true;
  } else if (action.type === "progress") {
    state[action.index].uploaded = Math.floor(action.value);
  } else if (action.type === "error") {
    state[action.index].uploaded = -1;
  }
  return [...state];
}
export function uploadFiles(
  filesToUpload: FileList,
  post: Post
): {
  files: ContentFile[];
  cancel: Function;
  success: boolean;
} {
  const [files, action] = useReducer<ContentFile[]>(filesReducer, []);
  let [mounted, setMountedFlag] = useState(true);

  useEffect(() => {
    const initialState: ContentFile[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      let file = filesToUpload[i];
      ((index) => {
        const task = uploadFile(
          file,
          post,
          async (path, meta) => {
            let content = await saveContent(post.id, {
              type: ContentType.FILE,
              actions: ["CREATING_THUMBNAIL"],
              value: path,
              meta,
            });
            createCloudBuildOp({
              operation: CLOUD_BUILD_OPERATIONS.create_thumbnail,
              payload: {
                postId: post.id,
                contentId: content.id as string,
              },
            });
            if (mounted) {
              action({ type: "success", index });
            }
          },
          (percentage) => {
            if (mounted) {
              action({ type: "progress", index, value: percentage });
            }
          },
          (err) => {
            if (mounted) {
              action({ type: "error", index });
            }
          }
        );
        initialState.push({ name: file.name, uploaded: 0, task, done: false });
      })(i);
    }
    action({ type: "initial", value: initialState });
    return () => {
      setMountedFlag((mounted = false));
    };
  }, []);

  function cancel() {
    (files as ContentFile[]).forEach((f) => f.task.cancel());
  }

  return {
    files,
    cancel,
    success:
      (files as ContentFile[]).length > 0 &&
      (files as ContentFile[]).every((f) => f.done === true),
  };
}

export function useLocalStorage(
  key: string,
  defaultValue?: any
): [(value) => void, () => any] {
  const isAvailable = typeof window.localStorage !== "undefined";
  let _value = defaultValue;
  const set = (value) => {
    if (isAvailable) {
      localStorage.setItem(key, (_value = value));
    }
  };
  const get = () => {
    if (isAvailable) {
      return localStorage.getItem(key);
    } else {
      return _value;
    }
  };
  if (
    isAvailable &&
    typeof defaultValue !== "undefined" &&
    localStorage.getItem(key) === null
  ) {
    set(defaultValue);
  }
  return [set, get];
}
